import { apiService } from './apiService';
import { API_BASE_URL } from '../config/apiConfig';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadResult {
  url: string;
  path: string;
  publicUrl: string;
}

class ImageUploadService {
  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  private readonly ALLOWED_TYPES = ['image/*']; // Allow any image type
  private readonly STORAGE_BUCKET = 'images';
  private bucketChecked = false; // Track if bucket has been checked

  // Lazy check - only verify bucket when actually uploading
  private async ensureBucketExists(): Promise<void> {
    // Only check once per session
    if (this.bucketChecked) {
      return;
    }

    this.bucketChecked = true;

    // Don't actually check - just mark as checked
    // Bucket existence will be verified during actual upload
    // This prevents unnecessary network requests on app load
  }

  // Validate file before upload
  private validateFile(file: File): { valid: boolean; error?: string } {
    // Check if it's an image file
    if (!file.type.startsWith('image/')) {
      return {
        valid: false,
        error: `Invalid file type. Please select an image file.`
      };
    }

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File too large. Maximum size: ${this.MAX_FILE_SIZE / (1024 * 1024)}MB`
      };
    }

    return { valid: true };
  }

  // Generate unique filename
  private generateFileName(file: File, category: string = 'general'): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const extension = file.name.split('.').pop() || 'jpg';
    return `${category}/${timestamp}_${randomString}.${extension}`;
  }

  // Compress image if needed
  private async compressImage(file: File, maxWidth: number = 1200, quality: number = 0.85): Promise<File> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          file.type,
          quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  }

  // Upload file to local proxy API
  async uploadImage(
    file: File,
    category: string = 'general',
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    try {
      console.log(`📤 Starting upload for file: ${file.name} (${file.size} bytes)`);

      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Compress image
      const compressedFile = await this.compressImage(file);
      console.log(`📦 Compressed file: ${compressedFile.size} bytes`);

      // Upload to local storage REST API (/api/images) using FormData
      console.log(`⬆️ Uploading via API`);

      const formData = new FormData();
      formData.append('image', compressedFile);
      formData.append('category', category);
      formData.append('name', file.name.split('.')[0]); // Name minus extension
      formData.append('active', 'true');

      // Use native fetch to bypass apiService since it strictly speaks JSON mapping
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
        credentials: 'include' // Important for session cookies
      });

      if (!response.ok) {
        let errorMsg = 'Upload failed';
        try {
          const errBody = await response.json();
          errorMsg = errBody.error || errorMsg;
        } catch (e) { }
        throw new Error(errorMsg);
      }

      const resData = await response.json();
      console.log('✅ Upload successful:', resData);

      if (onProgress) {
        onProgress({
          loaded: compressedFile.size,
          total: compressedFile.size,
          percentage: 100
        });
      }

      return {
        url: resData.url || resData.imageUrl || resData.data?.url,
        path: resData.url || resData.imageUrl || resData.data?.url,
        publicUrl: resData.url || resData.imageUrl || resData.data?.url
      };

    } catch (error) {
      console.error('❌ Image upload error:', error);
      throw error;
    }
  }

  // Delete image from local API
  async deleteImage(id: string): Promise<boolean> {
    try {
      await apiService.request(`/images/${id}`, { method: 'DELETE' });
      return true;
    } catch (error) {
      console.error('Delete error:', error);
      return false;
    }
  }

  // Get image URL (returns same since DB returns raw url string)
  getImageUrl(path: string): string {
    return path;
  }

  // Create multiple uploads
  async uploadMultipleImages(
    files: File[],
    category: string = 'general',
    onProgress?: (fileIndex: number, progress: UploadProgress) => void
  ): Promise<UploadResult[]> {
    const results: UploadResult[] = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const result = await this.uploadImage(
          files[i],
          category,
          (progress) => onProgress?.(i, progress)
        );
        results.push(result);
      } catch (error) {
        console.error(`Upload failed for file ${i}:`, error);
        throw error;
      }
    }

    return results;
  }

  // Get upload categories
  getCategories(): string[] {
    return [
      'team',
      'partners',
      'tours',
      'hero',
      'conservation',
      'community',
      'general'
    ];
  }

  // Get file info
  getFileInfo(file: File): {
    name: string;
    size: string;
    type: string;
    lastModified: Date;
  } {
    return {
      name: file.name,
      size: this.formatFileSize(file.size),
      type: file.type,
      lastModified: new Date(file.lastModified)
    };
  }

  // Format file size
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export const imageUploadService = new ImageUploadService();
